// libraries.
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

// slices.
import { apiAcquisitionsPortal } from '@store/apiSlices/apiSlices';

// interfaces.
import { ModelError } from '@store/apiSlices/interfaces/interfaces';

export interface MissingIntersection {
  p_min: number;
  p_max: number;
  overlap_area: number;
  iou: number;
  pct_of_smaller: number;
  type_min: string | null;
  type_max: string | null;
  comment_min: string | null;
  comment_max: string | null;
  // MySQL devuelve median_zone_id como INT, asi que aqui llegan numeros aunque
  // el resto de la app compare median_zone contra strings. Quien consuma esto
  // DEBE normalizar con String() antes de guardarlo en medianZoneCode.
  median_zone_min: string | number | null;
  median_zone_max: string | number | null;
  comparten_median_zone: boolean | number;
  // Geometria de cada miembro del par. Viene del backend para poder dibujar
  // el par sin depender de que su zona mediana cargue los polygons: hay pares
  // cuya zona no existe (data de prueba) o que no tienen zona.
  polygon_min: google.maps.LatLngLiteral[];
  polygon_max: google.maps.LatLngLiteral[];
}

export interface StaleIntersection {
  intersection_id: number;
  p_min: number;
  p_max: number;
  overlap_area_actual: number | null;
}

export interface DuplicateIntersection {
  p_min: number;
  p_max: number;
  n_rows: number;
  intersection_ids: string;
  list_polygons_variants: string;
  main_polygons: string;
}

export interface IntersectionAuditSummary {
  solapes_geometricos: number;
  pares_guardados: number;
  solapes_no_guardados: number;
  guardados_sin_solape: number;
}

export interface IntersectionAuditReport {
  summary: IntersectionAuditSummary;
  missing: MissingIntersection[];
  stale: StaleIntersection[];
  duplicates: DuplicateIntersection[];
}

interface IntersectionAuditParams {
  country?: string;
}

interface IntersectionAuditResponse {
  body: IntersectionAuditReport;
  status_code: number;
  success: boolean;
}

export const auditIntersections = apiAcquisitionsPortal.injectEndpoints({
  endpoints: builder => ({
    getIntersectionAudit: builder.query({
      query: ({ country }: IntersectionAuditParams) =>
        `/intersection/audit?country=${country}`,
      transformErrorResponse: (error: FetchBaseQueryError): ModelError => {
        const modelError: ModelError = {
          errorStatus: error.status,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          messageFromServer: (error.data as any)?.message,
          serviceErrorName: 'getIntersectionAudit',
        };
        return modelError;
      },
      transformResponse: (
        response: IntersectionAuditResponse
      ): IntersectionAuditReport => {
        return response.body;
      },
      // Comparte el tag 'getSavedIntersections': cuando se guarda una nueva
      // intersección (createIntersections invalida ese tag), la auditoría
      // queda stale y se refresca al volver a pedirla, reflejando el backfill.
      providesTags: ['getSavedIntersections'],
    }),
  }),
});

export const {
  useGetIntersectionAuditQuery,
  useLazyGetIntersectionAuditQuery,
} = auditIntersections;
